// frontend/src/staff/pages/StaffDashboard.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

export {}; // module safety

type AllowlistResponse = {
  allowed: boolean;
  reason?: string;
  staff_name?: string;
  role?: string;
  department?: string;
};

// ✅ Same shape used in StaffProgressList (only what we need for counts)
type MerchantSummary = {
  merchant_id: string;
  company_name: string;
  status?: string; // 進行中 / 完了 / 提出済（承認待ち） etc
  approval_status?: "未承認" | "承認済";
};



type SummaryResponse = {
  ok: boolean;
  total: number;
  approved: number;
  waitingApproval: number;
  inProgress: number;
};


const API_BASE = "/api";

const getStaffAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("staff_google_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};



export default function StaffDashboard() {
  const navigate = useNavigate();

  const email = localStorage.getItem("staff_email") || "";
  const name = localStorage.getItem("staff_name") || "";

  const [checking, setChecking] = useState(true);

  // ✅ Dashboard summary
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [counts, setCounts] = useState({
  total: 0,
  approved: 0,
  waitingApproval: 0,
  inProgress: 0,
});

  const forceLogout = (msg: string) => {
    localStorage.removeItem("staff_email");
    localStorage.removeItem("staff_name");
    localStorage.removeItem("staff_google_token");
    localStorage.removeItem("staff_role");
    localStorage.removeItem("staff_department");

    localStorage.setItem("staff_guard_error", msg);
    navigate("/staff", { replace: true });
  };

  // -------------------------
  // 1) Allowlist check
  // -------------------------
  useEffect(() => {
    const run = async () => {
      if (!email) {
        forceLogout("ログインが必要です。スタッフ用Googleアカウントでログインしてください。");
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/staff/check-allowlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });

        if (!res.ok) {
          forceLogout("ログイン制限チェックに失敗しました。管理者に連絡してください。");
          return;
        }

        const data: AllowlistResponse = await res.json();

        if (!data.allowed) {
          forceLogout("このアカウントは現在ログインできません。管理者に連絡してください。");
          return;
        }

        if (data.role) localStorage.setItem("staff_role", data.role);
        if (data.department) localStorage.setItem("staff_department", data.department);

        setChecking(false);
      } catch {
        forceLogout("ログイン制限チェックに失敗しました。管理者に連絡してください。");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------
  // 2) Fetch summary counts
  // -------------------------
  useEffect(() => {
    const runSummary = async () => {
      if (checking) return; // wait until allowlist check finishes

      setSummaryError("");
      setSummaryLoading(true);

      try {
        try {
  // ✅ NEW: fetch summary numbers directly from backend (permission filtered)
  const res = await fetch(`${API_BASE}/staff/progress/summary`, {
    headers: {
      ...getStaffAuthHeaders(),
    },
  });

  const json: SummaryResponse = await res.json().catch(() => ({
    ok: false,
    total: 0,
    approved: 0,
    waitingApproval: 0,
    inProgress: 0,
  }));

  if (!res.ok || !json.ok) {
    setSummaryError((json as any)?.detail || "サマリーの取得に失敗しました。");
    setSummaryLoading(false);
    return;
  }

  setCounts({
    total: json.total || 0,
    approved: json.approved || 0,
    waitingApproval: json.waitingApproval || 0,
    inProgress: json.inProgress || 0,
  });

  setSummaryLoading(false);
} catch {
  setSummaryError("バックエンドが起動しているか確認してください。");
  setSummaryLoading(false);
}

        setSummaryLoading(false);
      } catch {
        setSummaryError("バックエンドが起動しているか確認してください。");
        setSummaryLoading(false);
      }
    };

    runSummary();
  }, [checking]);

  // -------------------------
  // 3) Compute counts
  // -------------------------
  

  

  return (
    <StaffLayout title="スタッフダッシュボード" active="dashboard">
      {/* Top two boxes */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 14,
            padding: 14,
            background: "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>ログイン情報</div>
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            <div>名前：{name}</div>
            <div>メール：{email}</div>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 14,
            padding: 14,
            background: "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>管理メニュー</div>
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            ・加盟店アカウント作成
            <br />
            ・加盟店一覧管理
            <br />
            ・進捗確認
          </div>
        </div>
      </div>

      {/* ✅ Summary cards */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>サマリー</div>

        {summaryLoading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

        {summaryError && (
          <div
            style={{
              marginTop: 10,
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              color: "#b91c1c",
              padding: "10px 12px",
              borderRadius: 12,
              fontWeight: 900,
            }}
          >
            {summaryError}
          </div>
        )}

        {!summaryLoading && !summaryError && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <SummaryCard title="加盟店アカウント総数" value={counts.total} note="登録されている全件数" />
            <SummaryCard title="承認済み" value={counts.approved} note="承認済ステータス" />
            <SummaryCard
              title="提出済（承認待ち）"
              value={counts.waitingApproval}
              note="提出済だが未承認"
            />
            <SummaryCard
              title="入力途中（未提出）"
              value={counts.inProgress}
              note="未提出・入力中"
            />
          </div>
        )}
      </div>

      

      <div style={{ marginTop: 14, fontSize: 13, color: "#475569" }}>
        ※ 加盟店の確認・編集・削除は次の画面で行います。
      </div>
    </StaffLayout>
  );
}

function SummaryCard({ title, value, note }: { title: string; value: number; note?: string }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 14,
        background: "#fff",
        minHeight: 92,
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>{title}</div>
      <div style={{ marginTop: 10, fontSize: 26, fontWeight: 900, color: "#0b1f3b" }}>{value}</div>
      {note && <div style={{ marginTop: 6, color: "#64748b", fontWeight: 800, fontSize: 12 }}>{note}</div>}
    </div>
  );
}
